import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Briefcase, Loader2, MapPin } from "lucide-react";
import HammLogo from "@/components/landing/HammLogo";
import { API } from "@/context/AuthContext";
import JobApplicationForm from "@/components/career/JobApplicationForm";

const SITE = "https://www.hamm.gmbh";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
}

function CareerHeader() {
  return (
    <header className="border-b border-[#E3E8EC] bg-white/90 backdrop-blur-md sticky top-0 z-40">
      <div className="max-w-[1200px] mx-auto px-6 h-[76px] flex items-center justify-between">
        <Link to="/" className="flex items-center" aria-label="Zur Startseite" data-testid="career-logo">
          <HammLogo className="h-10 w-auto block" />
        </Link>
        <Link to="/" data-testid="career-back-home"
          className="flex items-center gap-2 px-5 h-10 rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:text-[#00AAA0] hover:border-[#00AAA0] transition-colors duration-200 font-tech text-[11px] tracking-[0.18em] uppercase">
          <ArrowLeft size={13} /> Zurück
        </Link>
      </div>
    </header>
  );
}

function setMeta(name, value, attr = "name") {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", value);
}

function setJsonLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}

export function CareerListPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    document.title = "Karriere · Hamm Entwicklung + Produktion GmbH";
    setMeta("description", "Offene Stellen bei Hamm EP GmbH in Geldern – Blechfertigung, Laser-/Kantbank-Bedienung und Vertrieb. Flache Hierarchien, familiäres Klima, internationale Kundschaft.");
    axios.get(`${API}/jobs`).then(r => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
    return () => { document.title = "Hamm Entwicklung + Produktion GmbH"; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#1d1d1b]" data-testid="career-list-page">
      <CareerHeader />
      <main className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
        <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#00AAA0] mb-4">Karriere</p>
        <h1 className="text-3xl lg:text-5xl font-bold tracking-tight">
          Werde Teil eines wachsenden{" "}
          <span className="text-[#2C57D2]">Fertigungsunternehmens.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-base sm:text-lg text-[#1d1d1b]/80 leading-relaxed">
          Flache Hierarchien, familiäres Klima und geregelte Kernarbeitszeiten – bei Hamm EP fertigen wir Blechbaugruppen für internationale Kundschaft aus Bahnindustrie, Medizin und Sanitärtechnik.
        </p>

        {loading ? (
          <div className="mt-16 text-center text-[#1d1d1b]"><Loader2 className="animate-spin inline mr-2" size={16}/> Lade offene Stellen…</div>
        ) : jobs.length === 0 ? (
          <div className="mt-16 border border-[#E3E8EC] rounded-lg px-8 py-14 text-center text-sm text-[#1d1d1b]">Aktuell sind keine Stellen ausgeschrieben. Initiativbewerbungen sind jederzeit willkommen an <a href="mailto:verkauf@hamm.gmbh" className="text-[#2C57D2] hover:text-[#00AAA0]">verkauf@hamm.gmbh</a>.</div>
        ) : (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="career-jobs-grid">
            {jobs.map(j => (
              <Link key={j.id} to={`/karriere/${j.slug}`} data-testid={`job-card-${j.slug}`}
                className="group flex flex-col border border-[#E3E8EC] rounded-lg overflow-hidden bg-white hover:-translate-y-1 hover:border-[#00AAA0] hover:shadow-[0_20px_60px_rgba(0,170,160,0.10)] transition-all duration-300">
                <div className="aspect-[16/9] bg-[#F7F9FA] overflow-hidden">
                  {j.image_url ? (
                    <img src={resolveImage(j.image_url)} alt={`Stellenbild ${j.title}`} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#00AAA0]"><Briefcase size={40} strokeWidth={1.2} /></div>
                  )}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-2 font-tech text-[10px] tracking-[0.22em] uppercase text-[#00AAA0]">
                    <MapPin size={12} /> Geldern (NRW)
                  </div>
                  <h2 className="text-lg font-semibold tracking-tight text-[#1d1d1b] mt-3">{j.title}</h2>
                  <p className="text-sm text-[#1d1d1b]/75 mt-3 leading-relaxed flex-1">{j.short_description}</p>
                  <span className="mt-6 self-start inline-flex items-center gap-2 font-tech text-[11px] tracking-[0.2em] uppercase text-[#2C57D2] group-hover:text-[#00AAA0] transition-colors">
                    Zur Stelle <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function CareerDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/jobs/by-slug/${slug}`).then(r => setJob(r.data)).catch(() => setJob(null)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!job) return;
    document.title = `${job.title} · Karriere bei Hamm EP GmbH`;
    setMeta("description", job.short_description);
    setMeta("og:title", `${job.title} · Hamm EP GmbH`, "property");
    setMeta("og:description", job.short_description, "property");
    setMeta("og:type", "article", "property");
    setJsonLd("jsonld-jobposting", {
      "@context": "https://schema.org", "@type": "JobPosting",
      "title": job.title, "description": job.full_description,
      "datePosted": job.created_at,
      "employmentType": "FULL_TIME",
      "hiringOrganization": {
        "@type": "Organization", "name": "Hamm Entwicklung + Produktion GmbH",
        "sameAs": SITE, "logo": `${SITE}/assets/hamm-logo.png`
      },
      "jobLocation": {
        "@type": "Place",
        "address": {"@type": "PostalAddress",
          "streetAddress": "Max-Planck-Str. 13", "addressLocality": "Geldern",
          "postalCode": "47608", "addressRegion": "NRW", "addressCountry": "DE"}
      },
      "directApply": true,
      "applicantLocationRequirements": {"@type": "Country", "name": "DE"},
      "url": `${SITE}/karriere/${job.slug}`,
    });
    return () => {
      const el = document.getElementById("jsonld-jobposting");
      if (el) el.remove();
      document.title = "Hamm Entwicklung + Produktion GmbH";
    };
  }, [job]);

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="animate-spin text-[#00AAA0]" /></div>;
  if (!job) return (
    <div className="min-h-screen bg-white text-[#1d1d1b]">
      <CareerHeader />
      <main className="max-w-[900px] mx-auto px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">Stelle nicht gefunden</h1>
        <button onClick={() => navigate("/karriere")} className="mt-6 px-6 h-11 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.2em] uppercase transition-colors">Zurück zur Übersicht</button>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-[#1d1d1b]" data-testid="career-detail-page">
      <CareerHeader />
      {/* Hero-Banner Ophardt-Style */}
      <section className="relative h-80 md:h-96 w-full overflow-hidden bg-[#1d1d1b]">
        {job.image_url && (
          <img src={resolveImage(job.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#00AAA0]">Offene Stelle</p>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight text-white max-w-4xl" data-testid="job-title">{job.title}</h1>
          <p className="mt-4 text-sm md:text-base text-white/85 font-tech tracking-wider">Geldern, Germany · Vollzeit · ab sofort</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="#bewerbung" data-testid="job-apply-btn"
              className="inline-flex items-center gap-2 px-7 h-12 rounded-md bg-[#00AAA0] hover:bg-[#00958c] text-white font-tech text-[11px] tracking-[0.2em] uppercase transition-colors">
              Ich bin interessiert / Jetzt bewerben
            </a>
            <a href={`mailto:?subject=${encodeURIComponent("Stellenangebot: " + job.title)}&body=${encodeURIComponent("Hier findest du das Stellenangebot: " + SITE + "/karriere/" + job.slug)}`} data-testid="job-share-btn"
              className="inline-flex items-center gap-2 px-7 h-12 rounded-md border-2 border-white/70 hover:border-[#2C57D2] hover:bg-[#2C57D2] text-white font-tech text-[11px] tracking-[0.2em] uppercase transition-colors">
              Job per E-Mail teilen
            </a>
          </div>
        </div>
      </section>

      <main className="max-w-[1200px] mx-auto px-6 py-14 lg:py-20">
        <Link to="/karriere" data-testid="job-back-list" className="inline-flex items-center gap-2 font-tech text-[11px] tracking-[0.2em] uppercase text-[#2C57D2] hover:text-[#00AAA0] transition-colors">
          <ArrowLeft size={13} /> Alle Stellen
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-10 gap-10 lg:gap-16">
          {/* Linke Spalte 70% */}
          <div className="lg:col-span-7">
            <p className="text-base sm:text-lg text-[#1d1d1b]/85 leading-relaxed">{job.short_description}</p>
            <article className="mt-8 job-content" data-testid="job-content"
              dangerouslySetInnerHTML={{ __html: job.full_description }} />

            <div id="bewerbung" className="mt-14 scroll-mt-24">
              <JobApplicationForm jobSlug={job.slug} jobTitle={job.title} />
            </div>
          </div>

          {/* Rechte Sidebar 30% */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 border border-[#E3E8EC] rounded-lg overflow-hidden bg-white">
              <div className="bg-[#1d1d1b] px-6 py-4">
                <p className="font-tech text-[11px] tracking-[0.28em] uppercase text-[#00AAA0]">Stelleninformationen</p>
              </div>
              <dl className="p-6 space-y-4 text-sm">
                {[
                  ["Startdatum", "ab sofort"],
                  ["Stellentyp", "Vollzeit"],
                  ["Stadt", "Geldern"],
                  ["Region", "Nordrhein-Westfalen"],
                  ["Land", "Deutschland"],
                ].map(([k, v]) => (
                  <div key={k} className="flex flex-col border-b border-[#EEF1F4] pb-3 last:border-0 last:pb-0">
                    <dt className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]/60">{k}</dt>
                    <dd className="mt-1 text-[#1d1d1b] font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="border-t border-[#E3E8EC] p-6 bg-[#F7F9FA]">
                <a href="#bewerbung"
                  className="w-full flex items-center justify-center gap-2 px-5 h-11 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.2em] uppercase transition-colors">
                  Jetzt bewerben
                </a>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <style>{`
        .job-content h3 { font-weight: 700; font-size: 1.25rem; color: #1d1d1b; margin: 2rem 0 0.75rem; padding-bottom: 0.5rem; border-bottom: 2px solid #00AAA0; display: inline-block; }
        .job-content h3:first-child { margin-top: 0; }
        .job-content p { margin: 0.75rem 0; color: #1d1d1b; line-height: 1.7; }
        .job-content ul { list-style: none; padding-left: 0; margin: 0.75rem 0 1.5rem; }
        .job-content li { position: relative; padding-left: 1.75rem; margin: 0.55rem 0; color: #1d1d1b; line-height: 1.65; font-size: 0.98rem; }
        .job-content li::before { content: ""; position: absolute; left: 0; top: 0.55rem; width: 8px; height: 8px; background: #00AAA0; transform: rotate(45deg); }
        .job-content strong { color: #2C57D2; font-weight: 600; }
      `}</style>
    </div>
  );
}
